import { ReactComponent as ArrowDownIcon } from 'assets/icons/arrow-down.svg';
import { RenderIf } from 'components/common/render-if';
import { Spinner } from 'components/common/spinner';
import { cn } from 'lib/utils';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { IOption } from 'types';

interface CustomSelectPrimaryProps {
    placeholder: string;
    options: IOption[];
    value?: string | null;
    onChange?: (value: string) => void;
    loading?: boolean;
    disabled?: boolean;
    className?: string;
}

const CustomSelectPrimary = ({
    options,
    placeholder,
    value,
    onChange,
    loading,
    disabled,
    className,
}: CustomSelectPrimaryProps) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedOption, setSelectedOption] = useState<IOption | null>();
    const selectRef = useRef<HTMLDivElement>(null);
    const { t } = useTranslation();

    function handleChange(option: IOption) {
        setSelectedOption(option);
        onChange && onChange(option.value);
        setIsOpen(false);
    }

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (selectRef.current && !selectRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    useEffect(() => {
        const selectedOption = options.find((option) => option.value === value);
        if (selectedOption) setSelectedOption(selectedOption);
        else setSelectedOption(null);
    }, [value]);
    return (
        <div
            ref={selectRef}
            className={cn('w-full relative select-none text-sm md:text-base', className)}
        >
            <button
                type="button"
                disabled={disabled}
                onClick={() => setIsOpen((prev) => !prev)}
                className="w-full text-txt-40 font-medium h-[40px] md:h-[49px] rounded-xl bg-[#F4F4F4] flex items-center justify-between px-3 md:px-5 cursor-pointer disabled:opacity-80 disabled:cursor-default"
            >
                {selectedOption ? (
                    <span className="text-txt-60">{selectedOption.label}</span>
                ) : (
                    placeholder
                )}
                <ArrowDownIcon
                    className={cn(
                        'duration-150 fill-txt-40 w-5 h-2.5 translate-y-0.5',
                        isOpen && 'rotate-180',
                    )}
                />
            </button>
            <RenderIf condition={isOpen}>
                <div className="w-full max-h-40 md:max-h-56 overflow-y-auto rounded-lg absolute mt-1 z-30 bg-[#F4F4F4]">
                    <RenderIf condition={!loading}>
                        {options.map((option) => (
                            <div
                                key={option.value}
                                onClick={() => handleChange(option)}
                                className={cn(
                                    'w-full px-3 md:px-5 py-2 hover:bg-dark-20 cursor-pointer',
                                    selectedOption?.value === option.value && 'bg-dark-20',
                                )}
                            >
                                {option.label}
                            </div>
                        ))}
                    </RenderIf>
                    <RenderIf condition={!!loading}>
                        <div
                            className={cn(
                                'w-full px-5 py-2 opacity-70 flex items-center gap-x-2 text-black',
                            )}
                        >
                            <Spinner size={14} color="black" />
                            {t('general.loading')}
                        </div>
                    </RenderIf>
                    <RenderIf condition={options.length === 0 && !loading}>
                        <div className="w-full px-5 py-2 text-center opacity-80">
                            {t('general.no-results-found')}
                        </div>
                    </RenderIf>
                </div>
            </RenderIf>
        </div>
    );
};

export default CustomSelectPrimary;
